import { useCallback, useMemo } from 'react';
import axiosInstance from '../../../config/axio.config';
import TimelineTable from '../timelineTable/timelineTable';

const lastDayOfMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();
const pad = (n) => String(n).padStart(2, '0');

// Monthly timeline: each row is a month and expands into the income sections and cost
// groups that made it up. Serves both the actuals (monthly-detail) and the forecast
// (projection-detail) — same response shape.
export default function MonthlyTimeline({
  months = [],
  title = 'Detalhamento mensal',
  endpoint = '/financeiro/reports/monthly-detail',
  emptyLabel = 'Sem movimentação no período',
}) {
  // The API returns each month's own balance; the running balance is computed here
  const { rows, totals } = useMemo(() => {
    let running = 0;
    const rows = months.map((m) => {
      const result = +(m.incomes - m.expenses).toFixed(2);
      running = +(running + result).toFixed(2);
      return {
        id: `${m.year}-${pad(m.month)}`,
        label: m.label,
        year: m.year,
        month: m.month,
        incomes: m.incomes,
        expenses: m.expenses,
        result,
        runningBalance: running,
      };
    });

    const totals = rows.reduce((acc, r) => ({
      incomes: +(acc.incomes + r.incomes).toFixed(2),
      expenses: +(acc.expenses + r.expenses).toFixed(2),
      result: +(acc.result + r.result).toFixed(2),
    }), { incomes: 0, expenses: 0, result: 0 });

    return { rows, totals };
  }, [months]);

  const loadDetail = useCallback(async (row) => {
    const { data } = await axiosInstance.get(endpoint, {
      params: {
        startDate: `${row.year}-${pad(row.month)}-01`,
        endDate: `${row.year}-${pad(row.month)}-${pad(lastDayOfMonth(row.year, row.month))}`,
      },
    });
    const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
    return {
      incomes: (data.incomes || []).map((section) => ({
        id: `i-${section.id}`,
        primary: section.name,
        secondary: section.count ? plural(section.count, 'lançamento', 'lançamentos') : null,
        value: section.value,
      })),
      expenses: (data.expenses || []).map((group) => ({
        id: `e-${group.id}`,
        primary: group.name,
        // Actuals group by cost center; the forecast only carries an entry count
        secondary: group.costCenters
          ? plural(group.costCenters, 'centro de custo', 'centros de custo')
          : (group.count ? plural(group.count, 'lançamento', 'lançamentos') : null),
        value: group.value,
      })),
    };
  }, [endpoint]);

  return (
    <TimelineTable
      title={title}
      rows={rows}
      totals={totals}
      periodColumnLabel="Mês"
      unitLabel={['mês', 'meses']}
      pageSize={12}
      loadDetail={loadDetail}
      emptyLabel={emptyLabel}
    />
  );
}
